import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  autoUpdate,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import CohortPercentileFilterForm from './CohortPercentileFilterForm';
import {
  IPercentileFilterInput,
  SimpleCohortTypeKey,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  ICohortRow,
  IRowMeta,
  setCohortRowPercentileFilter,
} from '../../../../../redux/slices/oldBuilder';
import { cohortConfigTypeSelector } from '../../../../../redux/selectors/builder';
import { assertNotNil } from '../../../../../utils/types';
import { getCohortPercentileFilterLabel } from '../../../../../utils/builder/cohorts';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { SingleSelectLabel } from '../../../../Dropdown/SingleSelect';
import CohortPercentileFilterPlaceholder from './CohortPercentileFilterPlaceholder';
import { HEADER_Z_INDEX } from '../../../../../styles/zIndexes';
import { floatingComponentStyles } from '../../../../../styles/shareableStyles';

interface IProps {
  rowMeta: IRowMeta;
  cohortRow: ICohortRow;
}

const FormContainer = styled.div`
  padding: 12px;
  min-width: 210px;
  z-index: ${HEADER_Z_INDEX};
  ${floatingComponentStyles};
`;

export default ({ rowMeta, cohortRow }: IProps) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const { simpleCohortTypeKey, percentileFilter } = cohortRow;

  const cohortConfigSelector = useAppSelector((state) =>
    cohortConfigTypeSelector(state, simpleCohortTypeKey),
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(5)],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const percentileOperators = useMemo(() => {
    if (
      cohortConfigSelector.__typename === 'ExpressionCohortConfig' &&
      simpleCohortTypeKey === SimpleCohortTypeKey.Expression
    ) {
      return cohortConfigSelector.percentileOperators;
    }
    if (
      cohortConfigSelector.__typename === 'MeasureCohortConfig' &&
      simpleCohortTypeKey === SimpleCohortTypeKey.Measure
    ) {
      return cohortConfigSelector.percentileOperators;
    }

    return [];
  }, [cohortConfigSelector, simpleCohortTypeKey]);

  const handleSubmit = useCallback(
    (input: IPercentileFilterInput) => {
      setIsOpen(false);
      dispatch(setCohortRowPercentileFilter({ rowMeta, input }));
    },
    [dispatch, rowMeta],
  );

  const handleReset = useCallback(() => {
    setIsOpen(false);
    if (percentileFilter) {
      dispatch(setCohortRowPercentileFilter({ rowMeta, input: null }));
    }
  }, [dispatch, percentileFilter, rowMeta]);

  const getLabel = () => {
    assertNotNil(percentileFilter);
    const operatorName = percentileOperators.find(
      (operator) => operator.key === percentileFilter.operator,
    )?.name;

    // In case if percentileOperators will not include operator from value
    assertNotNil(operatorName);

    const { operator, values } = percentileFilter;

    return getCohortPercentileFilterLabel({
      operatorKey: operator,
      operatorName,
      values,
    });
  };

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {percentileFilter ? (
          <SingleSelectLabel isAlwaysActive={true}>
            {getLabel()}
          </SingleSelectLabel>
        ) : (
          <CohortPercentileFilterPlaceholder />
        )}
      </div>
      {isOpen && (
        <FloatingPortal>
          <FormContainer
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <CohortPercentileFilterForm
              onSubmit={handleSubmit}
              onReset={handleReset}
              value={percentileFilter}
              percentileOperators={percentileOperators}
            />
          </FormContainer>
        </FloatingPortal>
      )}
    </>
  );
};
